/**
 * The HUD wears the zone's tome vibe (plan 17 phase 0) and the player's chosen
 * modular HUD theme/template (plan 17 phase 2 / Unification Plan Step 2).
 *
 * The HUD root takes `className="tome-tokens"` (the shared token sheet,
 * primitives/tome/tome-tokens.css) and the vibe's custom properties (tomeVibeStyle),
 * so any HUD piece can read --tome-* and --tome-hud-* colours without importing a
 * tome component.
 *
 * Resolves per-surface styles:
 * - 'mix' (default): A leather frame & bars + C vine tracker
 * - 'a': All A (Leather & Gilt)
 * - 'b': All B (Parchment Slips)
 * - 'c': All C (Ink & Vine)
 * - 'classic': Original Dark Ink & Brass (no [data-hud-style])
 */
import { useMemo, useState, useEffect } from 'react';
import type React from 'react';
import { TOME_VIBES, tomeVibeStyle, type TomeVibeName } from '../primitives/tome/tomeVibes';
import { zoneTomeVibe } from '../primitives/tome/zoneTomeVibes';
import { storage } from '../../services/StorageService';
import type { UiPrefs } from '../../../shared/userprefs/schema';
import '../primitives/tome/tome-tokens.css';

export const HUD_TOME_TOKENS_CLASS = 'tome-tokens';

export type HudConcreteStyle = 'a' | 'b' | 'c';

export interface HudTomeTokens {
  vibe: TomeVibeName;
  className: typeof HUD_TOME_TOKENS_CLASS;
  style: React.CSSProperties;
  hudStyle: UiPrefs['hudStyle'];
  hudVibe: UiPrefs['hudVibe'];
  frameStyle?: HudConcreteStyle;
  hotbarStyle?: HudConcreteStyle;
  chatStyle?: HudConcreteStyle;
  trackerStyle?: HudConcreteStyle;
}

/** The class and style that publish a vibe's tome tokens on an element. */
export function hudTomeTokens(
  vibe: TomeVibeName,
  hudStyle: UiPrefs['hudStyle'] = 'mix',
  hudVibe: UiPrefs['hudVibe'] = 'auto',
): HudTomeTokens {
  const isClassic = hudStyle === 'classic';
  return {
    vibe,
    className: HUD_TOME_TOKENS_CLASS,
    style: tomeVibeStyle(TOME_VIBES[vibe]),
    hudStyle,
    hudVibe,
    frameStyle: isClassic ? undefined : hudStyle === 'mix' ? 'a' : hudStyle,
    hotbarStyle: isClassic ? undefined : hudStyle === 'mix' ? 'a' : hudStyle,
    chatStyle: isClassic ? undefined : hudStyle === 'mix' ? 'a' : hudStyle,
    trackerStyle: isClassic ? undefined : hudStyle === 'mix' ? 'c' : hudStyle,
  };
}

/** The tome tokens and modular HUD styles for the current zone & player preferences. */
export function useHudTomeVibe(zoneId: string | null | undefined): HudTomeTokens {
  const [prefs, setPrefs] = useState(() => storage.loadUserPrefs().ui);

  useEffect(() => {
    return storage.subscribeUserPrefs((p) => {
      setPrefs(p.ui);
    });
  }, []);

  const hudStyle = prefs.hudStyle ?? 'mix';
  const hudVibe = prefs.hudVibe ?? 'auto';
  const activeVibe = hudVibe === 'auto' ? zoneTomeVibe(zoneId) : hudVibe;

  return useMemo(
    () => hudTomeTokens(activeVibe, hudStyle, hudVibe),
    [activeVibe, hudStyle, hudVibe],
  );
}
